import React, { useState, useEffect } from 'react';
import { X, Save, FileText, Tag, Folder, AlertCircle, Plus } from 'lucide-react';
import api from '../utils/api';
import RichTextEditor from './RichTextEditor';
import UserProfileModal from './UserProfileModal';


const NotesModal = ({ isOpen, onClose, client, projects, existingNote, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    project_id: '',
    category: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      
      if (existingNote) {
        setFormData({
          title: existingNote.title || '',
          content: existingNote.content || '',
          project_id: existingNote.project_id || '',
          category: existingNote.category || ''
        });
      } else {
        setFormData({
          title: '',
          content: '',
          project_id: '',
          category: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, existingNote]);

  const loadCategories = async () => {
    try {
      const response = await api.get(`/notes/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setCategories(response.data.map(cat => cat.category));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    // For rich text content, check if there's actual text content (not just HTML tags)
    const contentText = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!contentText) {
      newErrors.content = 'Content is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      let response;
      
      if (existingNote) {
        // Update existing note
        response = await api.put(
          `/notes/${existingNote.id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
      } else {
        // Create new note
        response = await api.post(
          `/clients/${client.id}/notes`,
          formData,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
      }
      
      // Handle mentions if any
      if (mentions.length > 0) {
        await handleMentions(response.data.id);
      }
      
      onSave(response.data);
      onClose();
    } catch (error) {
      console.error('Error saving note:', error);
      setErrors({
        submit: error.response?.data?.detail || 'Failed to save note. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMentions = async (noteId) => {
    try {
      // Process mentions through backend
      await api.post(
        `/mentions/process`,
        {
          content_type: 'note',
          content_id: noteId,
          content_text: formData.content,
          context: `Note: ${formData.title}`
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
    } catch (error) {
      console.error('Error processing mentions:', error);
      // Don't fail the entire save if mentions fail
    }
  };

  const handleContentChange = (content, extractedMentions = []) => {
    handleInputChange('content', content);
    setMentions(extractedMentions);
  };

  const handleMentionClick = (user) => {
    setSelectedUser(user);
    setShowUserProfile(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {existingNote ? 'Edit Note' : 'Create New Note'}
                </h2>
                <p className="text-blue-100 text-sm">
                  {client.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.title ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter note title..."
            />
            {errors.title && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Project Association */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Folder className="w-4 h-4 inline mr-1" />
              Associated Project (Optional)
            </label>
            <select
              value={formData.project_id}
              onChange={(e) => handleInputChange('project_id', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">No project association</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Tag className="w-4 h-4 inline mr-1" />
              Category (Optional)
            </label>
            <div className="flex space-x-2">
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select or enter category</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={categories.includes(formData.category) ? '' : formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                placeholder="Or type new category"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <RichTextEditor
              value={formData.content}
              onChange={handleContentChange}
              onMentionClick={handleMentionClick}
              placeholder="Enter your note content..."
              className={errors.content ? 'border-red-300' : ''}
              enableMentions={true}
              enableVariables={true}
              clientId={client?.id}
              contactId={null}
            />
            {errors.content && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.content}
              </p>
            )}
          </div>

          {/* Future: Create Task from Note Button */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-blue-900">Create Task from Note</h4>
                <p className="text-sm text-blue-700">Convert this note into a actionable task</p>
              </div>
              <button
                type="button"
                disabled
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg opacity-50 cursor-not-allowed"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Create Task
              </button>
            </div>
            <p className="text-xs text-blue-600 mt-2">This feature will be available soon</p>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                {errors.submit}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : (existingNote ? 'Update Note' : 'Create Note')}</span>
            </button>
          </div>
        </form>

        {/* User Profile Modal */}
        <UserProfileModal
          isOpen={showUserProfile}
          onClose={() => setShowUserProfile(false)}
          userId={selectedUser?.id}
          userName={selectedUser?.name}
        />
      </div>
    </div>
  );
};

export default NotesModal;